import React from 'react';
import { twMerge } from 'tailwind-merge';

import { Badge, BadgeText } from '@/components/ui/badge';

export type PillTone = 'brand' | 'neutral' | 'info' | 'success' | 'warning' | 'error';

/**
 * Tone → container + text classes. Design tokens only — never hardcode a
 * color at a call site. Keep the two halves in sync so text stays legible
 * on its background in both themes.
 */
const PILL_TONE_CLASSES: Record<PillTone, { container: string; text: string }> = {
  brand: { container: 'bg-primary-500', text: 'text-typography-white' },
  neutral: { container: 'bg-background-200', text: 'text-typography-700' },
  info: { container: 'bg-background-info', text: 'text-info-700' },
  success: { container: 'bg-background-success', text: 'text-success-700' },
  warning: { container: 'bg-background-warning', text: 'text-warning-700' },
  error: { container: 'bg-background-error', text: 'text-error-700' },
};

interface PillProps {
  /** Pill label. */
  children: React.ReactNode;
  /** Visual tone, mapped to token-based background + text colors. */
  tone?: PillTone;
  /** Extra container classes (merged over the tone background). */
  className?: string;
  /** Extra label classes (merged over the tone text color). */
  textClassName?: string;
}

/**
 * Rounded label pill for short status/category text.
 *
 * Built on the Gluestack `Badge` primitive, which styles itself with
 * NativeWind classes that compile to real CSS — so it renders correctly on
 * web. (An inline RN `paddingHorizontal` / `backgroundColor` style on a
 * `cssInterop`-wrapped view does NOT reliably apply on web and an array
 * `style` on one throws; this component exists so call sites stop hand-rolling
 * that broken pattern.)
 *
 * The `Badge` defaults are overridden for a true pill: `rounded-full` instead
 * of `rounded-sm`, and `normal-case` instead of the badge's uppercase text.
 */
export function Pill({ children, tone = 'neutral', className, textClassName }: PillProps) {
  const toneClasses = PILL_TONE_CLASSES[tone];

  return (
    <Badge className={twMerge('rounded-full', toneClasses.container, className)}>
      <BadgeText className={twMerge('font-medium normal-case', toneClasses.text, textClassName)}>
        {children}
      </BadgeText>
    </Badge>
  );
}
